import { Link, useOutletContext } from "react-router";

import { ArrowRightIcon, BugIcon, FolderKanbanIcon, PlusIcon, TicketIcon, UsersIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

import CreateProjectForm from "@/features/projects/components/CreateProjectForm";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";

export default function Home() {
  const { permissions } = useOutletContext() as { permissions: string[] };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1">
        <Card className="col-span-full border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BugIcon className="size-5 text-primary" />
              <Badge variant="secondary">Getting started</Badge>
            </div>
            <CardTitle className="mt-1 text-2xl">Welcome to BugTracker</CardTitle>
            <CardDescription className="max-w-lg">BugTracker helps your team capture, assign, and resolve issues before they become problems. Follow the steps below to set up your first project.</CardDescription>
          </CardHeader>
          <CardFooter className="flex-col sm:flex-row gap-3">
            <CreateProjectForm
              permissions={permissions}
              tooltipClassName="w-full sm:w-auto"
              trigger={
                <Button className="w-[100%] sm:w-auto">
                  <PlusIcon />
                  Create project
                </Button>
              }
            />
            {permissions.includes(PERMISSIONS.USER_PROJECTS_READ) && (
              <Button className="w-full sm:w-auto" variant="outline" asChild>
                <Link to="/projects" className="w-full sm:w-auto">
                  Browse projects
                </Link>
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
      <div>
        <h2 className="font-heading text-xs font-semibold tracking-wider text-muted-foreground uppercase mb-4">How it works</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card size="sm" className="flex-1">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex size-9 shrink-0 items-center justify-center bg-primary/10 text-primary">
                  <FolderKanbanIcon className="size-4" />
                </div>
                <span className="font-heading text-3xl font-bold tabular-nums text-muted-foreground/30 leading-none">01</span>
              </div>
              <CardTitle className="mt-4 text-sm">Create a project</CardTitle>
              <CardDescription>Start by creating a project to group all related tickets, members, and activity in one place.</CardDescription>
            </CardHeader>
          </Card>
          <Card size="sm" className="flex-1">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex size-9 shrink-0 items-center justify-center bg-primary/10 text-primary">
                  <UsersIcon className="size-4" />
                </div>
                <span className="font-heading text-3xl font-bold tabular-nums text-muted-foreground/30 leading-none">02</span>
              </div>
              <CardTitle className="mt-4 text-sm">Invite members</CardTitle>
              <CardDescription>Add your team and assign roles - Admin, Developer, or Submitter - to control what each person can do.</CardDescription>
            </CardHeader>
          </Card>
          <Card size="sm" className="flex-1">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex size-9 shrink-0 items-center justify-center bg-primary/10 text-primary">
                  <TicketIcon className="size-4" />
                </div>
                <span className="font-heading text-3xl font-bold tabular-nums text-muted-foreground/30 leading-none">03</span>
              </div>
              <CardTitle className="mt-4 text-sm">Log, track & resolve</CardTitle>
              <CardDescription>Create tickets for bugs, tasks, or feature requests. Assign them, monitor progress in the project overview, and close them as issues get resolved.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
}
